import type { ExecutionPlanBatch } from '../../hooks/useExecutionPlan'

export interface AutoConfirmCostSettings {
  image: boolean
  video: boolean
}

export const DEFAULT_AUTO_CONFIRM_COST_SETTINGS: AutoConfirmCostSettings = {
  image: true,
  video: false,
}

// Mirrors agentic_workflow.core.auto_confirm_cost. Keep these policy lists in
// sync until the cost class is generated from node schemas
// (tests/test_backend_auto_confirm_cost.py enforces the sync).
const IMAGE_COST_NODE_TYPES = new Set([
  'character_designer',
  'location_designer',
  'object_designer',
  'image_generator',
  'sound_expert',
  'speech_generator',
  'webtoon_panel_generator',
  'storyboard_designer',
  'storyboard_no_enhance_designer',
])

const VIDEO_COST_NODE_TYPES = new Set([
  'video_generator',
])

type AutoConfirmCostClass = 'image' | 'video' | 'free' | 'manual'

// Video Generator no longer owns Dreamwrite audio authoring actions. Sound
// Expert audio generation uses the image/audio cost setting via its node type.
const VIDEO_GENERATOR_AUDIO_COST_ACTIONS = new Set<string>([])

function classifyCostNode(
  type: string,
  action?: string | null,
  sparks?: number | null,
): AutoConfirmCostClass {
  // A node quoted at zero spends nothing, so it never needs a manual cost card.
  // Every node in a durable batch is quoted, priced or not (Video Editor,
  // Script Writer, free actions).
  if (sparks != null && sparks <= 0) return 'free'
  if (!action || action === 'generate') {
    if (VIDEO_COST_NODE_TYPES.has(type)) return 'video'
    if (IMAGE_COST_NODE_TYPES.has(type)) return 'image'
    return 'manual'
  }
  if (
    type === 'video_generator' &&
    VIDEO_GENERATOR_AUDIO_COST_ACTIONS.has(action)
  ) {
    return 'image'
  }
  return 'manual'
}

function canAutoConfirmNodeTypes(
  nodes: Array<{ type: string; action?: string | null; sparks?: number | null }>,
  settings: AutoConfirmCostSettings,
): boolean {
  if (nodes.length === 0) return false

  return nodes.every(({ type, action, sparks }) => {
    const costClass = classifyCostNode(type, action, sparks)
    if (costClass === 'free') return true
    if (costClass === 'image') return settings.image
    if (costClass === 'video') return settings.video
    return false
  })
}

export function canAutoConfirmBatch(
  batch: ExecutionPlanBatch,
  settings: AutoConfirmCostSettings,
): boolean {
  if (batch.status !== 'pending') return false
  return canAutoConfirmNodeTypes(
    batch.entries.map((entry) => ({
      type: entry.type,
      action: entry.action,
      sparks: entry.sparks,
    })),
    settings,
  )
}
